import { isUndefined, pick } from "lodash";
import { ReactNode, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { useRouter, Route, useSearchParams, sanitizeRedirect } from "@sol/routing";
import { usePrevious } from "@sol/utils";

import { useCookiePreferencesStore } from "./useCookiePreferencesStore";

type Props = {
    placeholder?: ReactNode;
    children: ReactNode;
};

export function CookiePolicyGuard({ placeholder = null, children }: Props) {
    const { isPolicyAccepted: isPolicyAccepted } = useCookiePreferencesStore(
        useShallow(state => pick(state, ["isPolicyAccepted"])),
    );
    const oldIsAccepted = usePrevious(isPolicyAccepted);

    const router = useRouter();
    const cookiesRoute = ([Route.COOKIE_POLICY, Route.COOKIE_PREFERENCES] as const).find(
        route => router.pathname === route,
    );

    const redirect = sanitizeRedirect(useSearchParams().redirect);

    const shouldRedirect = !isPolicyAccepted && !cookiesRoute;

    useEffect(() => {
        if (isPolicyAccepted) {
            if (!cookiesRoute) {
                // Not concerned by cookie policy, do nothing
                return;
            } else if (!isUndefined(oldIsAccepted) && !oldIsAccepted) {
                // The user just accepted cookie policy, redirect them to their target page
                if (redirect) {
                    router.push({ route: redirect });
                } else if (!router.canGoBack) {
                    router.push({ route: Route.HOME });
                } else {
                    router.back();
                }
                return;
            } else if (cookiesRoute === Route.COOKIE_POLICY) {
                // The user visit cookie policy page but has already accepted
                router.replace({ route: Route.COOKIE_PREFERENCES, query: { redirect } });
            }
        } else if (!cookiesRoute) {
            // If the user has not accepted cookie policy and is not on the cookie policy routes, redirect them to the cookie policy page
            router.replace({
                route: Route.COOKIE_POLICY,
                query: { redirect: router.asPath },
            });
        } else {
            // If the user is on the GDPR routes
            return;
        }
    }, [isPolicyAccepted, cookiesRoute, redirect]);

    if (shouldRedirect) {
        return placeholder;
    }

    return children;
}
